import React, { useEffect, useRef, useState } from 'react';
import { constructMediaUrl } from '../../../api/helpers';
import { shouldRetryVideoError, videoRetryDelay } from '../videoError';
import { SlideOutput, groupSlideOutputs } from './slideOutputs';
import { useSlideTags } from './useSlideTags';

const IMAGE_RETRY_LIMIT = 2;

/** One slide: its clip over its still, or the still alone. A failed load is
 *  retried quietly (re-keyed element, no cache-buster), like ClipPlayer. */
const SlideTile: React.FC<{ slide: SlideOutput; label: string }> = ({ slide, label }) => {
  const [attempts, setAttempts] = useState(0);
  const retryTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(
    () => () => {
      if (retryTimer.current) clearTimeout(retryTimer.current);
    },
    []
  );
  const retryIf = (allowed: boolean) => {
    if (!allowed) return;
    retryTimer.current = setTimeout(() => setAttempts((count) => count + 1), videoRetryDelay(attempts));
  };

  return (
    <li className="border border-[var(--border-subtle)] bg-black">
      <p className="px-1.5 py-1 text-[9px] font-mono uppercase tracking-widest text-zinc-400">
        {`${label}${slide.video ? ' · video' : ''}`}
      </p>
      {slide.video ? (
        <video
          key={attempts}
          data-testid="slide-output-video"
          aria-label={label}
          src={constructMediaUrl(slide.video)}
          poster={slide.still ? constructMediaUrl(slide.still) : undefined}
          className="w-full"
          controls
          playsInline
          // The still is the poster: fetch the clip only once it is played.
          preload={slide.still ? 'none' : 'metadata'}
          onError={(event) => retryIf(shouldRetryVideoError(event.currentTarget.error?.code, attempts))}
        />
      ) : (
        <img
          key={attempts}
          src={constructMediaUrl(slide.still || '')}
          alt={label}
          className="w-full"
          loading="lazy"
          onError={() => retryIf(attempts < IMAGE_RETRY_LIMIT)}
        />
      )}
    </li>
  );
};

/** Every slide of a mixed render, by slide number; a video slide plays over its still. */
const SlideOutputStrip: React.FC<{ urls: string[] }> = ({ urls }) => {
  const tags = useSlideTags(urls);
  if (!tags) {
    return (
      <p data-testid="slide-output-loading" className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
        Loading slides…
      </p>
    );
  }

  const slides = groupSlideOutputs(urls, tags);
  // Untagged tiles are numbered on their own: after "Slide 2", a tile called
  // "Output 3" would read like a third slide.
  const hasNumbered = slides.some((slide) => slide.index !== null);
  let extra = 0;
  return (
    <ol className="grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="slide-output-strip">
      {slides.map((slide) => {
        const label =
          slide.index !== null ? `Slide ${slide.index}` : `${hasNumbered ? 'Extra output' : 'Output'} ${(extra += 1)}`;
        return <SlideTile key={`${slide.still ?? ''}|${slide.video ?? ''}`} slide={slide} label={label} />;
      })}
    </ol>
  );
};

export default SlideOutputStrip;
